import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Modal, FormField, SelectField } from '../components/modal';
import { Button } from '@/components/ui/button';
import {
  Stack, Student, ChalkboardTeacher, ChartBar, Plus, CaretRight, WarningCircle, ArrowClockwise,
} from '@phosphor-icons/react';
import {
  getMySchoolId, getDirectionStatus, listClassrooms, createClassroom, listSchoolStudents, listTeachers, listLevels,
  type Classroom, type Level,
} from '../lib/direction';

export default function DirectionHome() {
  const navigate = useNavigate();
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [classes, setClasses] = useState<Classroom[]>([]);
  const [kpi, setKpi] = useState({ active: 0, inactive: 0, teachers: 0 });
  const [levels, setLevels] = useState<Level[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    (async () => {
      try {
        const status = await getDirectionStatus().catch(() => null);
        if (!cancelled && status?.needs_onboarding) { navigate('/direction/onboarding'); return; }
        const sid = await getMySchoolId();
        if (!sid) { if (!cancelled) setLoading(false); return; }
        if (!cancelled) setSchoolId(sid);
        const [cls, students, teachers, lv] = await Promise.all([
          listClassrooms(sid), listSchoolStudents(sid), listTeachers(sid), listLevels().catch(() => []),
        ]);
        if (cancelled) return;
        setClasses(cls);
        setKpi({ active: students.filter((s) => s.active).length, inactive: students.filter((s) => !s.active).length, teachers: teachers.length });
        setLevels(lv as Level[]);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, reloadKey]);

  const [showNewClass, setShowNewClass] = useState(false);
  const [ncName, setNcName] = useState('');
  const [ncLevelId, setNcLevelId] = useState('');
  const [saving, setSaving] = useState(false);
  const submitNewClass = async () => {
    if (!schoolId || !ncName.trim() || !ncLevelId || saving) return;
    setSaving(true);
    try {
      const c = await createClassroom(schoolId, ncName, ncLevelId);
      setClasses((p) => [...p, c]);
      setShowNewClass(false); setNcName(''); setNcLevelId('');
    } finally { setSaving(false); }
  };

  const levelOptions = levels.map((l) => ({ value: l.id, label: l.label }));
  const levelLabel = (lid: string) => levels.find((l) => l.id === lid)?.label ?? null;
  const levelAbbr = (c: Classroom) =>
    (levelLabel(c.level_id) ?? c.name).replace(/ème$/, 'e').replace(/ère$/, 're').split(' ')[0] ?? c.name;

  const [showArchived, setShowArchived] = useState(false);
  const activeClasses = classes.filter((c) => !c.archived);
  const archivedClasses = classes.filter((c) => c.archived);
  const visibleClasses = showArchived ? archivedClasses : activeClasses;

  return (
    <>
      <HeaderContent
        width="md"
        showBurger
        eyebrow="Mode direction"
        eyebrowIcon={<ChartBar size={11} weight="bold" />}
      >
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Tableau de bord</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Classes, élèves et enseignants de votre établissement.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[960px] mx-auto px-5 pb-16 pt-4">

        {loading ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Chargement…</p>
        ) : failed ? (
          <div className="flex flex-col items-start gap-3 py-4">
            <p className="flex items-start gap-2 m-0 text-[13.5px] text-[var(--naia-text-primary)]">
              <WarningCircle size={18} weight="fill" className="text-destructive shrink-0 mt-px" />
              <span>Vos classes et effectifs n'ont pas pu être chargés. Votre établissement est intact.</span>
            </p>
            <Button variant="outline" size="sm" className="min-h-[44px]" onClick={() => setReloadKey((k) => k + 1)}>
              <ArrowClockwise size={15} weight="bold" />
              Réessayer
            </Button>
          </div>
        ) : !schoolId && classes.length === 0 ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Aucun établissement rattaché à votre compte.</p>
        ) : (
          <>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(155px,1fr))] gap-3 mb-8">
              <KpiCard icon={<Stack size={18} />} value={String(activeClasses.length)} label="Classes" />
              <KpiCard icon={<Student size={18} />} value={String(kpi.active)} label="Élèves actifs" />
              <KpiCard icon={<Student size={18} />} value={String(kpi.inactive)} label="Élèves inactifs" muted />
              <KpiCard icon={<ChalkboardTeacher size={18} />} value={String(kpi.teachers)} label="Enseignants" />
            </div>

            <div className="flex items-center gap-2.5 mb-3 flex-wrap">
              <span className="text-[10.5px] font-extrabold tracking-[.12em] uppercase text-[var(--naia-text-secondary)]">
                Classes
              </span>
              {visibleClasses.length > 0 && (
                <span className="text-[10px] font-black min-w-[18px] h-[18px] px-1.5 rounded-full flex items-center justify-center text-primary bg-primary/10">
                  {visibleClasses.length}
                </span>
              )}
              {archivedClasses.length > 0 && (
                <label className="flex items-center gap-2 text-[12px] font-semibold text-[var(--naia-text-secondary)] whitespace-nowrap">
                  <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                  Afficher les classes archivées
                </label>
              )}
              <Button className="ml-auto" onClick={() => setShowNewClass(true)}>
                <Plus size={14} /> Nouvelle classe
              </Button>
            </div>

            {visibleClasses.length === 0 ? (
              <p className="text-[var(--naia-text-secondary)] py-3 text-[13.5px]">
                {showArchived ? 'Aucune classe archivée.' : 'Aucune classe. Créez-en une.'}
              </p>
            ) : (
              <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden divide-y divide-[var(--naia-line)]">
                {visibleClasses.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => navigate(`/direction/classe/${c.id}`)}
                    className="flex items-center gap-3 w-full px-4 py-[13px] bg-transparent border-none cursor-pointer text-left font-[inherit] hover:bg-accent transition-colors"
                  >
                    <span className="flex-shrink-0 w-9 h-9 rounded-[var(--naia-r-sm)] flex items-center justify-center text-[11px] font-black text-primary bg-primary/10">
                      {levelAbbr(c)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[15px] text-[var(--naia-text-primary)]">{c.name}</div>
                      {levelLabel(c.level_id) && (
                        <div className="text-[12px] text-[var(--naia-text-secondary)] font-medium">{levelLabel(c.level_id)}</div>
                      )}
                    </div>
                    <CaretRight size={16} weight="bold" className="text-[var(--naia-text-secondary)] shrink-0 opacity-40" />
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <Modal
        open={showNewClass}
        title="Nouvelle classe"
        onClose={() => setShowNewClass(false)}
        footer={<>
          <Button variant="secondary" onClick={() => setShowNewClass(false)}>Annuler</Button>
          <Button onClick={submitNewClass} disabled={!ncName.trim() || !ncLevelId || saving}>
            {saving ? 'Création…' : 'Créer'}
          </Button>
        </>}
      >
        <FormField label="Nom de la classe" value={ncName} onChange={setNcName} placeholder="Ex : 5ème B" autoFocus onEnter={submitNewClass} />
        <SelectField label="Niveau" value={ncLevelId} onChange={setNcLevelId} options={levelOptions} placeholder="Choisir un niveau…" />
      </Modal>
    </>
  );
}

function KpiCard({ icon, value, label, muted = false }: { icon: ReactNode; value: string; label: string; muted?: boolean }) {
  return (
    <div className="flex flex-col gap-2 p-4 bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)]">
      <div className="flex items-start justify-between">
        <span className={`text-[30px] font-black leading-none ${muted ? 'text-[var(--naia-text-secondary)]' : 'text-primary'}`}>
          {value}
        </span>
        <span className="opacity-25 mt-0.5 text-[var(--naia-text-primary)]">{icon}</span>
      </div>
      <span className="text-[13px] font-semibold text-[var(--naia-text-secondary)]">{label}</span>
    </div>
  );
}
